import {
  Button,
  SmartContentDisplay,
  useDisplayModeContext,
} from '@act/storybook';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Divider, IconButton, Typography, useTheme } from '@mui/material';
import HtmlDisplayer from 'react-components/components/html-displayer';
import useAddActionDialog from 'react-components/dialog-forms/hooks/use-add-action-dialog';
import useInteractionsDialog from 'react-components/dialog-forms/hooks/use-interactions-dialog';
import Loader from 'react-components/workflows/loader';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hooks';
import { needsSelectors } from 'redux/slices/needs.slice';
import { generateDatadogTrackingProps } from 'toolkit/environment/environment/user-monitoring';
import { InfoContainer, LabeledDatum } from 'toolkit/styles/dataDisplay';
import {
  FullScreenCard,
  FullScreenCardActions,
  FullScreenCardContent,
  FullScreenDialog,
  FullScreenDialogContent,
  FullScreenDialogTitle,
} from 'toolkit/styles/fullScreenDialogComponents';
import { GenericDialogProps } from 'utilities/dialog-context/dialog-context-helpers';
import NeedHistory from '../components/history/need-history';
import NeedStatusTag from '../components/need-status-tag';
import syvaNeedsUtils from '../syva-needs-utils';
import NeedTimeAlert from './components/need-time-alert';
import './need-detail-dialog.styles.scss';

export interface NeedDetailUniqueProps {
  needId: number;
}

export type NeedDetailDialogProps = GenericDialogProps<void> &
  NeedDetailUniqueProps;

const NeedDetailDialog = ({ open, setOpen, needId }: NeedDetailDialogProps) => {
  const theme = useTheme();
  const { displayMode } = useDisplayModeContext();
  const needMetadata = metaSliceSelectors.useSelectNeedsMetadata();
  const openAddActionDialog = useAddActionDialog();
  const need = needsSelectors.useSelectNeedById(needId);
  const member = membersAppSelectors.useSelectMemberById(need.memberId);
  const { createCall } = useInteractionsDialog();

  const memberIsIndependent = member?.memberStage?.key === 'independence';

  function closeDialog() {
    setOpen(false);
  }

  if (needMetadata.status !== 'success' || !need) {
    return (
      <FullScreenDialog
        open={open}
        onClose={closeDialog}
        displayMode={displayMode}
      >
        <Loader />
      </FullScreenDialog>
    );
  }

  const needEditable = syvaNeedsUtils.needIsEditable(need, member);
  const issue = needMetadata.data.issues.find(i => i.id === need.issueId);
  const category = needMetadata.data.categories.find(
    c => c.id === issue?.categoryId
  );
  const domain = needMetadata.data.domains.find(
    d => d.id === category?.domainId
  );

  return (
    <FullScreenDialog
      open={open}
      onClose={closeDialog}
      displayMode={displayMode}
    >
      <FullScreenDialogTitle>
        <div className="title-container">
          <IconButton onClick={closeDialog}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="titleLarge">{issue.label}</Typography>
          <NeedStatusTag need={need} />
        </div>
      </FullScreenDialogTitle>
      <FullScreenDialogContent>
        <FullScreenCard>
          <FullScreenCardContent>
            <div className="info">
              <InfoContainer
                style={{ backgroundColor: theme.palette.navyShades[50] }}
              >
                <LabeledDatum label={'Domain'} datum={domain?.label} />
                <LabeledDatum label={'Category'} datum={category?.label} />
                <LabeledDatum label={'Issue'} datum={issue?.label} />
                <LabeledDatum
                  label={'Reason'}
                  datum={
                    <SmartContentDisplay maxHeight={150}>
                      <HtmlDisplayer innerHtml={need?.reason} />
                    </SmartContentDisplay>
                  }
                />
              </InfoContainer>
            </div>
            <div className="alert">
              <NeedTimeAlert need={need} />
            </div>
            <div className="divider">
              <Divider />
            </div>
            <div className="history-header">
              <Typography variant="titleMedium">History</Typography>
              <Box sx={{ justifySelf: 'flex-end' }}>
                {needEditable && (
                  <Box>
                    <Button
                      variant="text"
                      debounced
                      onClick={() =>
                        createCall({
                          user: member,
                          need: need,
                          isCustomResourcing: true,
                        })
                      }
                      startIcon={<AddIcon />}
                      {...generateDatadogTrackingProps(
                        'need-detail-dialog-call-resource'
                      )}
                      sx={{ marginRight: '8px' }}
                      disabled={memberIsIndependent || !!need?.fulfilledAt}
                    >
                      Call resource
                    </Button>
                    <Button
                      variant="text"
                      onClick={() => openAddActionDialog({ need })}
                      startIcon={<AddIcon />}
                      {...generateDatadogTrackingProps(
                        'need-detail-dialog-add-action'
                      )}
                      disabled={memberIsIndependent || !!need?.fulfilledAt}
                    >
                      Action
                    </Button>
                  </Box>
                )}
              </Box>
            </div>
            <NeedHistory
              need={need}
              editMode={needEditable}
              detailMode={true}
            />
          </FullScreenCardContent>
          <FullScreenCardActions>
            <Button onClick={closeDialog}>Close</Button>
          </FullScreenCardActions>
        </FullScreenCard>
      </FullScreenDialogContent>
    </FullScreenDialog>
  );
};

export default NeedDetailDialog;
